'use client'

/**
 * The story autopilot in the browser (docs/demo-autopilot.md). The server
 * sends what the autopilot waits for and, for the minimum time or the safety
 * cap, the seconds left; the browser counts those seconds down between polls
 * and words the wait for the header chip and the Presenter. Nothing here
 * moves the story: only a Continue press, a decision or a real event does.
 */

import { useEffect, useMemo, useState, useSyncExternalStore } from 'react'
import { autopilotRunning } from '@/lib/story'
import type { StoryAutopilot, StoryState, StoryStepId } from '@/lib/dr-types'

/** "20 s", "2 min 12 s", "4 min". */
export function fmtCountdown(seconds: number): string {
  const s = Math.max(0, Math.round(seconds))
  if (s < 60) return `${s} s`
  const min = Math.floor(s / 60)
  const rest = s % 60
  return rest ? `${min} min ${rest} s` : `${min} min`
}

/**
 * Seconds left until the autopilot's next move, counted down once a second
 * from the last answer. Null while it waits on an event, and frozen while it is
 * paused.
 */
export function useAutopilotCountdown(autopilot: StoryAutopilot | null | undefined): number | null {
  const due = autopilot?.due_in_seconds
  const paused = !!autopilot?.paused
  // Each new answer (a new object from the poll) restarts the count from its own figure.
  const deadline = useMemo(
    () => (typeof due === 'number' && Number.isFinite(due) ? Date.now() + due * 1000 : null),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- the answer object marks a fresh figure
    [autopilot, due]
  )
  const [now, setNow] = useState(() => Date.now())

  useEffect(() => {
    if (deadline === null || paused) return
    setNow(Date.now())
    const timer = setInterval(() => setNow(Date.now()), 1000)
    return () => clearInterval(timer)
  }, [deadline, paused])

  if (deadline === null || typeof due !== 'number') return null
  if (paused) return Math.max(0, Math.round(due))
  return Math.max(0, Math.round((deadline - now) / 1000))
}

export interface ContinueToken {
  step_id: StoryStepId | null
  wait_index: number | null
}

export interface AutopilotWaitText {
  /** The chip's sentence: the wait label, "Next: Posts sensed in 9 s", or the label and " · moves on in 45 s". */
  text: string
  /** The sentence without its countdown, and the countdown, so a narrow chip can truncate the one and keep the other. */
  lead: string
  countdown: string | null
  /** Seconds left to the minimum time or the cap, or null. */
  secondsLeft: number | null
  dueReason: 'min_time' | 'cap' | null
  /** Continue would be accepted for this wait now. */
  canContinue: boolean
  /** Continue was pressed and the step moves once the minimum time has passed. */
  continued: boolean
  /** What Continue leads to ("Posts sensed"), for the button's name and the notice. */
  nextTitle: string | null
  /** Sent with Continue, so a press for a wait that has already moved on is ignored. */
  token: ContinueToken
  paused: boolean
}

/**
 * What the autopilot waits for, worded for the header chip (plans/10 5.1).
 * Null when the autopilot is off, absent, or the story is before the
 * earthquake or at the report.
 */
export function useAutopilotWait(story: Pick<StoryState, 'step_id' | 'autopilot'> | null): AutopilotWaitText | null {
  const autopilot = story?.autopilot ?? null
  const secondsLeft = useAutopilotCountdown(autopilot)
  if (!story || !autopilot || !autopilotRunning(story)) return null
  const label = autopilot.waiting_for?.label?.trim() ?? ''
  const nextTitle = autopilot.next_title?.trim() || null
  if (!label && !nextTitle) return null
  const dueReason = autopilot.due_reason ?? null
  const base = {
    secondsLeft,
    dueReason,
    canContinue: !!autopilot.can_continue,
    continued: !!(autopilot.continued || autopilot.waiting_for?.continued),
    nextTitle,
    token: { step_id: autopilot.step_id ?? null, wait_index: autopilot.wait_index ?? null },
    paused: !!autopilot.paused,
  }
  const fallback = label || `Next: ${nextTitle}`
  // Paused: the count stands still, so the chip says so and drops it.
  if (autopilot.paused) return { ...base, text: `Paused · ${fallback}`, lead: `Paused · ${fallback}`, countdown: null }
  if (secondsLeft !== null && dueReason === 'min_time' && nextTitle) {
    const countdown = `in ${fmtCountdown(secondsLeft)}`
    const lead = `Next: ${nextTitle}`
    return { ...base, text: `${lead} ${countdown}`, lead, countdown }
  }
  if (secondsLeft !== null && dueReason === 'cap') {
    const countdown = `· moves on in ${fmtCountdown(secondsLeft)}`
    return { ...base, text: `${fallback} ${countdown}`, lead: fallback, countdown }
  }
  return { ...base, text: fallback, lead: fallback, countdown: null }
}

// ─── Continue notices ──────────────────────────────────────────────────────

/**
 * The answer to a Continue press, shown inline where it was pressed: under
 * the header chip (chip and stage shortcut) or in the Presenter's Autopilot
 * panel. A refusal is the server's sentence, as it is.
 */
export interface ContinueNotice {
  id: number
  text: string
  tone: 'info' | 'refusal'
  surface: 'header' | 'panel'
  /** The wait the press was for (`waitKey`): a refusal stays until it is dismissed or the wait changes. */
  waitKey: string
}

/** "s4:0": the step and wait a Continue token names, compared with the wait the last poll shows. */
export function waitKey(token: ContinueToken): string {
  return `${token.step_id ?? ''}:${token.wait_index ?? ''}`
}

/** The Continue token of the wait the story shows now (the step id stands in when the autopilot names none, as at the report). */
export function storyToken(story: Pick<StoryState, 'step_id' | 'autopilot'> | null): ContinueToken {
  return { step_id: story?.autopilot?.step_id ?? story?.step_id ?? null, wait_index: story?.autopilot?.wait_index ?? null }
}

let notice: ContinueNotice | null = null
let noticeSeq = 0
const noticeListeners = new Set<() => void>()

function emitNotice(): void {
  noticeListeners.forEach((listener) => listener())
}

export function showContinueNotice(
  text: string,
  tone: ContinueNotice['tone'],
  surface: ContinueNotice['surface'],
  token: ContinueToken
): void {
  noticeSeq += 1
  notice = { id: noticeSeq, text, tone, surface, waitKey: waitKey(token) }
  emitNotice()
}

export function clearContinueNotice(id?: number): void {
  if (!notice || (id !== undefined && notice.id !== id)) return
  notice = null
  emitNotice()
}

function subscribeNotice(listener: () => void): () => void {
  noticeListeners.add(listener)
  return () => noticeListeners.delete(listener)
}

export interface ContinueNoticeState {
  notice: ContinueNotice | null
  /** Spread on the notice: hovering or focusing it holds an info notice on screen (WCAG 2.2.1). */
  holdProps: {
    onMouseEnter: () => void
    onMouseLeave: () => void
    onFocus: () => void
    onBlur: () => void
  }
}

/**
 * The latest Continue notice for one surface. An info notice clears itself
 * after `ttlMs`, counted while it is not hovered or focused. A refusal stays
 * until it is dismissed or the wait on screen (`currentWaitKey`) changes.
 */
export function useContinueNotice(surface: ContinueNotice['surface'], currentWaitKey: string, ttlMs = 8000): ContinueNoticeState {
  const current = useSyncExternalStore(
    subscribeNotice,
    () => notice,
    () => null
  )
  const shown = current && current.surface === surface ? current : null
  const [hovered, setHovered] = useState(false)
  const [focused, setFocused] = useState(false)
  const held = hovered || focused

  useEffect(() => {
    if (!shown || shown.tone !== 'info' || held) return
    const timer = setTimeout(() => clearContinueNotice(shown.id), ttlMs)
    return () => clearTimeout(timer)
  }, [shown, held, ttlMs])

  useEffect(() => {
    if (shown && shown.tone === 'refusal' && shown.waitKey !== currentWaitKey) clearContinueNotice(shown.id)
  }, [shown, currentWaitKey])

  useEffect(() => {
    if (shown) return
    setHovered(false)
    setFocused(false)
  }, [shown])

  const holdProps = useMemo(
    () => ({
      onMouseEnter: () => setHovered(true),
      onMouseLeave: () => setHovered(false),
      onFocus: () => setFocused(true),
      onBlur: () => setFocused(false),
    }),
    []
  )
  return { notice: shown, holdProps }
}
